/* eslint-disable dot-notation */
/**
 * Axios 관련 유틸리티
 * @exports {@link utils}
 * @packageDocumentation
 */
import axios, { AxiosRequestConfig, AxiosInstance as AxiosModuleInstance, AxiosResponse } from 'axios-jsonp-pro'
// import axios, { AxiosRequestConfig, AxiosInstance as AxiosModuleInstance, AxiosResponse } from 'axios'
import * as webUtils from './web-utils'
import { globalStorage } from './storage/global-storage'
import logger from './log-utils'

const REQUEST_INTERCEPTORS_STORAGES_KEY = '__REQUEST_AXIOS_INTERCEPTOR_KEY__'
const RESPONSE_INTERCEPTORS_STORAGES_KEY = '__RESPONSE_AXIOS_INTERCEPTOR_KEY__'

/**
 * 인터셉터 범위
 */
export enum InterceptorScope {
  BOTH = 'BOTH',
  SERVER = 'SERVER',
  BROWSER = 'BROWSER',
}

export enum DebugInterceptorMode {
  NONE = 'NONE',
  ONLY_ERROR = 'ONLY_ERROR',
  ALL = 'ALL',
}

export interface AxiosInstance extends AxiosModuleInstance {}
export interface ExtendAxiosRequestConfig extends AxiosRequestConfig {
  /**
   * axiosInstance
   */
  axiosInstance?: AxiosInstance

  /**
   * 디버깅 로그 옵션
   */
  debugInterceptorMode?: DebugInterceptorMode

  /**
   * jsonp 포맷 여부
   */
  jsonp?: boolean
}
export interface ExtendsAxiosResponse<T = any> extends AxiosResponse<T> {}

export interface AxiosRequestInterceptorArgs {
  onFulfilled?: (value: ExtendAxiosRequestConfig) => ExtendAxiosRequestConfig | Promise<ExtendAxiosRequestConfig> | void
  onRejected?: (error: any) => any
  key: string
  scope: InterceptorScope
  index?: number
}
export interface AxiosResponseInterceptorArgs {
  onFulfilled?: (value: ExtendsAxiosResponse) => ExtendsAxiosResponse | Promise<ExtendsAxiosResponse> | void
  onRejected?: (error: any) => any
  key: string
  scope: InterceptorScope
  index?: number
  processAllError?: boolean
}

/**
 * axios 기반의 API Call 기능
 * @param config axios 요청 config
 * @typeParam T Response 데이터 유형
 * @returns
 * @export
 */
export const call = async <T = any>(config: ExtendAxiosRequestConfig): Promise<ExtendsAxiosResponse<T>> => {
  const axiosInstance: AxiosInstance = config.axiosInstance ? config.axiosInstance : axios

  if (config.jsonp) {
    const res = await axiosInstance.jsonp<T>(config.url, config)
    return res as ExtendsAxiosResponse<T>
  } else {
    const res = await axiosInstance.request<T>(config)
    return res as ExtendsAxiosResponse<T>
  }
}

/**
 * axios의 인스턴스를 생성한다.
 * @param config axios config
 * @returns axiosInstance
 * @export
 */
export const createInstance = (config: ExtendAxiosRequestConfig = {}): AxiosInstance => {
  if (!webUtils.isBrowser()) {
    if (!config.headers) {
      config.headers = {}
    }

    if (!config.headers.common) {
      config.headers.common = {}
    }
    config.headers.common['accept-encoding'] = 'gzip, deflate'
  }
  const axiosInstnace = axios.create(config)
  axiosInstnace['CancelToken'] = axios.CancelToken
  axiosInstnace['isCancel'] = axios.isCancel

  // add interceptor for debug
  if (config.debugInterceptorMode && config.debugInterceptorMode !== 'NONE') {
    const requestInterceptor: AxiosRequestInterceptorArgs = {
      key: 'debug',
      scope: InterceptorScope.BOTH,
      onRejected: err => {
        logger().error('ajax error', err)
        throw err
      },
    }

    const responseInterceptor: AxiosResponseInterceptorArgs = {
      key: 'debug',
      scope: InterceptorScope.BOTH,
      onRejected: err => {
        logger().error('ajax error', err)
        throw err
      },
      processAllError: true,
    }

    if (config.debugInterceptorMode === 'ALL') {
      requestInterceptor.onFulfilled = v => {
        // logger().debug(`axios request : \n url: ${v.url} \n headers: ${JSON.stringify(v.headers, null, 2)} \n data: ${JSON.stringify(v.data)} `)
      }
      responseInterceptor.onFulfilled = v => {
        // @ts-ignore
        // logger().debug(`axios response : \n status: ${v.status} \n headers: ${JSON.stringify(v.headers, null, 2)} \n data: ${JSON.stringify(v.data)} `)
      }
    }

    getRequestInterceptors().unshift(requestInterceptor)
    getResponseInterceptors().unshift(responseInterceptor)
  }

  getRequestInterceptors().forEach(interceptor => addRequestInterceptorToInstance(axiosInstnace, interceptor))
  getResponseInterceptors().forEach(interceptor => addResponseInterceptorToInstance(axiosInstnace, interceptor))

  return axiosInstnace
}

export const getRequestInterceptors = () => {
  const item = globalStorage.getItem(REQUEST_INTERCEPTORS_STORAGES_KEY)

  if (!item) {
    globalStorage.setItem(REQUEST_INTERCEPTORS_STORAGES_KEY, [])
    return globalStorage.getItem(REQUEST_INTERCEPTORS_STORAGES_KEY)
  }

  return item
}

export const getResponseInterceptors = () => {
  const item = globalStorage.getItem(RESPONSE_INTERCEPTORS_STORAGES_KEY)

  if (!item) {
    globalStorage.setItem(RESPONSE_INTERCEPTORS_STORAGES_KEY, [])
    return globalStorage.getItem(RESPONSE_INTERCEPTORS_STORAGES_KEY)
  }

  return item
}

const addRequestInterceptorToInstance = (instance: AxiosModuleInstance, interceptor: AxiosRequestInterceptorArgs) => {
  if (
    interceptor.scope === InterceptorScope.BOTH ||
    (interceptor.scope === InterceptorScope.BROWSER && webUtils.isBrowser()) ||
    (interceptor.scope === InterceptorScope.SERVER && !webUtils.isBrowser())
  ) {
    interceptor.index = instance.interceptors.request.use(
      interceptor.onFulfilled ? config => interceptor.onFulfilled(config) || config : undefined,
      interceptor.onRejected ? error => interceptor.onRejected(error) || Promise.reject(error) : undefined,
    )
  }
}

const addResponseInterceptorToInstance = (instance: AxiosModuleInstance, interceptor: AxiosResponseInterceptorArgs) => {
  if (
    interceptor.scope === InterceptorScope.BOTH ||
    (interceptor.scope === InterceptorScope.BROWSER && webUtils.isBrowser()) ||
    (interceptor.scope === InterceptorScope.SERVER && !webUtils.isBrowser())
  ) {
    const onRejected = error => {
      if (!interceptor.processAllError) {
        // 최초 인터셉터에서 처리된 오류
        if (error.isHttpNetworkError) {
          return Promise.reject(error)
        }
      }

      if (interceptor.onRejected) {
        return interceptor.onRejected(error)
      } else {
        return Promise.reject(error)
      }
    }

    interceptor.index = instance.interceptors.response.use(interceptor.onFulfilled ? config => interceptor.onFulfilled(config) || config : undefined, onRejected)
  }
}

/**
 * request 인터셉터를 추가한다.
 * @param interceptor 인터셉터
 * @param instance 인스턴스 (없을 경우, 대기)
 */
export const addRequestInterceptor = (interceptor: AxiosRequestInterceptorArgs, instance?: AxiosInstance) => {
  const interceptors = getRequestInterceptors()

  if (instance) {
    addRequestInterceptorToInstance(instance, interceptor)
    interceptors.push(interceptor)
    return
  }

  for (let i of interceptors) {
    if (i.key === interceptor.key) {
      i = interceptor
      return
    }
  }

  interceptors.push(interceptor)
}

/**
 * response 인터셉터를 추가한다.
 * @param interceptor 인터셉터
 * @param instance 인스턴스 (없을 경우, 대기)
 */
export const addResponseInterceptor = (interceptor: AxiosResponseInterceptorArgs, instance?: AxiosInstance) => {
  const interceptors = getResponseInterceptors()

  if (instance) {
    addResponseInterceptorToInstance(instance, interceptor)
    interceptors.push(interceptor)
    return
  }

  for (let i of interceptors) {
    if (i.key === interceptor.key) {
      i = interceptor
      return
    }
  }

  interceptors.push(interceptor)
}

/**
 * 통신 오류 메시지를 조회
 * @param error
 */
export const getAxiosErrorMessage = (error: Error) => {
  const err: any = error
  if (!err.request && !err.response) {
    return err.message
  }

  if (err.message && error.message.includes('timeout')) {
    return '통신 시간이 만료되었습니다. 다시 시도해주십시오. 동일한 현상이 나타나는 경우, 관리자에게 문의하시기 바랍니다.'
  } else if (err.response) {
    switch (err.response.status) {
      case 500:
      case 501:
      case 502:
      case 503:
        return '서버 장애가 발생하였습니다. 관리자에게 문의하시기 바랍니다.'

      case 504:
        return '서버 연결이 원활하지 않습니다. 관리자에게 문의하시기 바랍니다.'
      case 400:
      case 405:
        return '서버에 연결할 수 없습니다. 네트워크 환경을 점검하시기 바랍니다.'
      case 401:
        return '수행 권한이 없습니다.'
    }
  }
  return err.message
}
